/**
 * ErrorState — Crop Circle error-state primitive.
 *
 * Renders a centered error icon, title, optional message, and optional retry
 * action. All text uses Crop Circle tokens/styled Text — never bare Paper Text.
 *
 * The error palette uses tokens.colors.semantic.error (red) for the icon/title
 * tint; the body message uses the neutral secondary tone.
 */
import React from 'react';
import { StyleSheet, Text, View, ViewStyle } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { colors, spacing, typography } from '../crop-circle/tokens';

/** Default error icon (alert circle in semantic-error red). */
const DefaultErrorIcon: React.FC = () => (
  <Svg width={32} height={32} viewBox="0 0 32 32" fill="none">
    <Path
      d="M16 3C8.82 3 3 8.82 3 16s5.82 13 13 13 13-5.82 13-13S23.18 3 16 3z"
      fill={colors.semantic.errorLight}
    />
    <Path
      d="M16 10v7"
      stroke={colors.semantic.error}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <Path
      d="M16 21v1"
      stroke={colors.semantic.error}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </Svg>
);

export interface ErrorStateProps {
  /** Primary error title. Defaults to 'Something went wrong'. */
  title?: string;
  /** Optional error detail / body message. */
  message?: string;
  /** Optional icon override. Defaults to the alert-circle icon. */
  icon?: React.ReactNode;
  /** Optional action element (e.g. a "Retry" Button). */
  action?: React.ReactNode;
  /** Outer container style override. */
  style?: ViewStyle;
  /** Test ID for testing. */
  testID?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  icon,
  action,
  style,
  testID,
}) => (
  <View style={[styles.container, style]} testID={testID}>
    <View style={styles.iconWrap}>{icon ?? <DefaultErrorIcon />}</View>
    <Text style={styles.title}>{title}</Text>
    {message ? <Text style={styles.message}>{message}</Text> : null}
    {action && <View style={styles.actionWrap}>{action}</View>}
  </View>
);

ErrorState.displayName = 'ErrorState';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing['2xl'],
    gap: spacing.sm,
  },
  iconWrap: {
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.label.medium,
    fontWeight: '600',
    color: colors.semantic.error,
    textAlign: 'center',
  },
  message: {
    ...typography.label.medium,
    color: colors.neutral[400],
    textAlign: 'center',
  },
  actionWrap: {
    marginTop: spacing.sm,
  },
});
